/**
 * Form controller.
 *
 * This class controls all common form functionalities.
 */

// Use jQuery as $ within this file scope.
const $ = jQuery;

import Common from './common';

/**
 * Class Forms
 */
export default class Forms {

    /**
     * Class constructor is for binding class properties.
     */
    constructor() {
    }

    /**
     * Cache dom elements for use in the class's methods
     */
    cache() {
        this.cacheCustomSelectBoxes();

        if( ! this.$customSelects.length > 0 ) {
            return;
        }
    }

    /**
     * This method queries all custom select box elements.
     *
     * Note! This currently works only for a single custom select box on a page.
     */
    cacheCustomSelectBoxes() {

        // Find all custom select boxes.
        this.$customSelects = $( '.js-form__custom-select' );

        // Buttons that toggles the dropddown checkbox areas.
        this.$selectButtons = this.$customSelects.find( '.js-form__custom-select__btn' );

        // The dropdown areas that will be given an `search-form__custom-select__inner--open` class when dropdown open.
        this.$selectInnerAreas = this.$customSelects.find( '.js-form__custom-select__inner' );

        // The checkbox input elements inside the dropdowns.
        this.$selectLists = this.$customSelects.find( '.js-form__custom-select__list' );

        // This is the element displayed for a closed select element.
        this.$selectButtonClosed = this.$customSelects.find( '#js-form__custom-select__btn__txt--closed' );

        // These are the hidden radio buttons holding the select box values.
        this.$selectRadios = this.$customSelects.find( '.js-form__custom-select__list__item__radio' );

        // If a select box value is clicked with a mouse, we close the dropdown.
        this.$selectBoxLabels = this.$customSelects.find( '.js-form__custom-select__list__item__label' );
    }

    /**
     * All common events
     * @return {void}
     */
    events() {

        // Check if user has clicked outside an open checkbox dropdown
        document.addEventListener( 'click', e => this.checkOutsideDropdDownClick( e ) );

        // If a target is selected, change the select box title.
        if( this.$selectRadios.length > 0 ) {
            this.$selectRadios.change( e => this.changeValue( e.target ) );
        }
    }

    /**
     * Checks if clicked outside of dropdownbox.
     *
     * @link https://stackoverflow.com/a/3028037
     *
     * @param {object} e Event object.
     * @return {void}
     */
    checkOutsideDropdDownClick( e ) {
        if ( ! $( e.target ).closest( '.js-form__custom-select__inner' ).length ) {
            this.closeDropDowns();
        }
    }

    /**
     * Closes all dropdown boxes.
     *
     * @return {void}
     */
    closeDropDowns() {
        if ( this.$selectInnerAreas ) {
            Common.removeClassFromAll( this.$selectInnerAreas, 'form__custom-select__inner--open' );
        }

        if ( this.$selectButtons ) {
            Common.setAttrOnAll( this.$selectButtons, 'aria-expanded', false );
        }

        if ( this.$selectLists ) {
            Common.setAttrOnAll( this.$selectLists, 'aria-hidden', true );
        }
    }

    /**
     * Loops custom select box values and sets the default value
     * if one is selected on page load.
     */
    setInitialValueForSelect() {
        if ( this.$selectRadios.length > 0 ) {
            this.$selectRadios.each( ( i, elem ) => {
                if ( elem.checked ) {
                    this.changeValue( elem );
                }
            } );
        }
    }

    /**
     * Sets the custom select box selected value text.
     *
     * @param {object} changeObj Could be the actual change event or a passed element.
     */
    changeValue( changeObj ) {

        // Safely get the changed radio button element.
        const $target = $( changeObj.target || changeObj );

        //  Bail early if the event object is incomplete.
        if ( $target.length === 0 ) {
            return;
        }

        // Set the selection visible.
        const selectionLabel = $target.data( 'label' );
        this.$selectButtonClosed.html( selectionLabel );

        // Close all after selection.
        this.closeDropDowns();

        // The input might have a callback method.
        const controller = $target.data( 'cb-ctrl' );
        const command    = $target.data( 'cb-cmd' );

        // If a callback was defined, we can run it with the Theme class.
        // Pass the actual DOM node.
        const controllerInstance = Theme.getController( controller );

        // If controller was found, try to run the method.
        if ( controllerInstance && typeof controllerInstance[command] === 'function' ) {
            controllerInstance[command].call( controllerInstance, changeObj );
        }

    }

    /**
     * Toggles state of checkbox dropdown area
     *
     * @param {object} e Event object.
     * @return {void}
     */
    toggleDropdown( e ) {
        Theme.Common.stop( e );

        // The clicked element
        let toggleLink = e.target;

        // The toggleLink might contain a span with the class 'js-form__custom-select__title'. Check if we clicked that.
        if ( toggleLink.classList.contains( 'js-form__custom-select__btn__txt' ) ) {
            toggleLink = e.target.parentNode;
        }

        // Close all other dropdowns if we are about to open this one
        if ( ! toggleLink.parentNode.classList.contains( 'form__custom-select__inner--open' ) ) {
            this.closeDropDowns();
        }

        // Toggle class of checkbox button parent
        toggleLink.parentNode.classList.toggle( 'form__custom-select__inner--open' );

        // Get the '<ul>' list that comes right after the button
        const dropdownList = toggleLink.nextElementSibling;

        // Get the aria state of the clicked button and toggle aria-expanded state
        const ariaExpandedState = toggleLink.getAttribute( 'aria-expanded' ) === 'false' ? true : false;
        toggleLink.setAttribute( 'aria-expanded', ariaExpandedState );

        // Toggle aria-hidden state of <ul> list that contains all the <li> elements with the actual checkboxes
        dropdownList.setAttribute( 'aria-hidden', ! ariaExpandedState );
    }

    /**
     * Add CSS-class if there is quiz on page.
     */
    addQuizClass() {
        if ( $( '.gquiz-field' ).length ) {
            $( 'body' ).addClass( 'quiz-page' );
        }
    }

    /**
     * Run when the document is ready.
     */
    docReady() {
        this.addQuizClass();
        this.cache();
        this.events();
        this.setInitialValueForSelect();
    }
}
